// ============================================================================
// src/lib/broilerBatchesApi.ts — the ppp-v4 write base, taken at SAVE time
// ----------------------------------------------------------------------------
// `ppp-v4` is ONE app_store document holding the farm's WHOLE broiler batch
// history — 30 rows and 36,006 bytes of JSON on production as of 2026-08-19.
// Every write of it replaces the entire array, and until now every writer built
// that array by mapping over `BatchesContext.batches`: the copy App published
// during its legacy boot.
//
// THE DEFECT. `batches` is `[]` until the boot fan-out lands, and it stays `[]`
// forever on a mount where the legacy boot never ran or failed. Eight call sites
// then compute their next array from that empty base:
//
//     upd (autosave)   batches.map(...)              -> []      TOTAL WIPE
//     submit           [...batches, batch]           -> [batch] 29 ROWS LOST
//     del              batches.filter(...)           -> []      TOTAL WIPE
//     documents x3     batchRows.map(...)            -> []      TOTAL WIPE
//     status flips x2  batches.map(...)              -> []      TOTAL WIPE
//
// and hand it to App's `persist`, which upserts it verbatim. There is no
// migration ledger on production to roll such a write back from. This module
// exists so that the base is the array as the SERVER holds it at the moment the
// operator saved, not as some earlier render happened to hold it.
//
// WHY A "MERGE" AND NOT SIMPLY "READ FRESH AND REBUILD". Rebuilding from the
// fresh read would lose the opposite way: `persist` is fire-and-forget, so two
// saves inside one debounce window can overlap, and the second one's fresh read
// may legitimately predate the first one's write. Today the second save stands
// on the first one's in-memory result and keeps it. So the caller's array stays
// AUTHORITATIVE for every row it names, and the fresh read contributes only the
// rows the caller never mentioned. The output is a superset of today's, never a
// subset — which is why this cannot regress a booted mount.
//
// THE NON-CLOBBERING IS STRUCTURAL, NOT REMEMBERED. Following
// `resolveFeedOrdersSavePlan`, which merges a per-surface PATCH so unnamed keys
// pass through by reference, a row this module carries over is the SAME OBJECT
// the read produced — never re-derived, never re-projected, never normalized.
// A future caller cannot drop a row by forgetting to preserve it; it can only
// drop a row by handing that row to `removed`, which is a thing you have to
// write down. `del` is the only caller that does.
//
// WHICH IS ALSO WHY THE BOOT REPAIR IS NOT NEEDED HERE, AND MUST NOT BE ADDED.
// src/app/data/migratedSurfaces/broiler.batches.index.ts warns that
// `BatchesContext.batches` is not `app_store['ppp-v4']` — the boot runs
// `applyBroilerBootRepair` over the raw blob and publishes the RESULT — and that
// "a RAW read would revert every persisted repair on the next save". That is
// true of a renderer that PROJECTS the raw blob and then persists its
// projection. It is not true here, because this module re-derives nothing: rows
// the caller named come from the caller (already repaired, since the caller's
// base is the repaired context array), and rows the caller did not name are
// written back byte-for-byte as they were just read. Writing a row back exactly
// as the server holds it cannot revert anything. Adding the repair here would
// instead import a CLOCK dependency and a second app_store key into a save path,
// and would let the two live lifecycle rules (hatch-day activation, which also
// stamps feed costs, and the processing-date advance) fire on rows the operator
// never touched, at a moment no test covers.
//
// IDENTITY IS id-OR-name, AND BOTH HALVES ARE LOAD-BEARING. App's own call sites
// key by `id` — `batch.id = editId || String(Date.now())`. But five of the seven
// e2e ppp-v4 seeds (tests/scenarios/*_seed.js) write rows carrying only `name`,
// and `applyPppV4Update` in src/lib/broiler.ts already treats `name` as this
// blob's identity. So a row is matched on EITHER, and a fresh row is carried
// only when it shares NO identity with anything the caller named. That direction
// is deliberate: a false match drops a row the caller has, which is merely
// today's behaviour, while a false miss would DUPLICATE it. Measured on
// production: 30 rows, 30 distinct ids, 30 distinct names, zero rows missing
// either.
//
// FAIL CLOSED, AND THE FOUR REFUSALS ARE NOT ONE REFUSAL. A save that cannot
// establish a base writes NOTHING and says so through the same `saveStatus`
// indicator every app_store write on these surfaces already drives. A silent
// no-op is the defect this module removes; a partial write is worse than both.
// `read-failed` and `read-malformed` follow feedOrdersApi exactly. `base-unkeyed`
// and `next-unkeyed` are this blob's own: a row with neither an id nor a name
// cannot be proved present or absent, so it can be neither carried (it might
// duplicate) nor dropped (it might be history).
//
// AN ABSENT ROW IS NOT AN UNAVAILABLE ONE, AND HERE THAT IS EVIDENCED RATHER
// THAN ASSUMED. app_store's RLS policies are symmetric — read USING
// `auth.uid() IS NOT NULL`, insert/update WITH CHECK the same (verified against
// production 2026-08-19) — so no session that could perform the upsert can be
// denied the select. A `maybeSingle()` that resolves to no row therefore means
// the farm genuinely has no broiler history yet, not that ours is hidden, and
// `[]` is the correct base for it.
//
// THE DOCUMENT PATCH, AND WHY IT IS HERE RATHER THAN AT THE CALL SITE. Seven of
// the eight writers above legitimately hold a batch array: they are editing,
// creating or deleting a batch, and the array is the thing they are editing.
// The three document handlers never were — they held it only to have something
// to `map` over on the way to a whole-blob write, which is the entire content of
// `legacy.context.batches.batches` on src/broiler/BatchForm.tsx. Naming the row
// and its new document list instead lets that form read NOTHING, and the base it
// is applied to is the save-time read this module already takes. On a mount with
// no boot state the old expression yielded `[]` for any callback, so the upload
// reached storage and then vanished from the blob without a word; the patch
// lands on the carried row instead, and refuses out loud when it matches none.
//
// WHAT THIS MODULE DELIBERATELY DOES NOT OWN. The WRITE. App's `persist` still
// calls App's `sbSave`, so the retry/8s-race/never-throws/setSaveStatus
// semantics in src/lib/appStoreBlobApi.ts stay exactly where they are and this
// change adds no second app_store writer to the inventory. It also does not
// touch the optimistic `setBatches(nb)` every call site performs first: the
// screen still repaints immediately, and a refusal leaves the operator looking
// at their edit with an error indicator, which is the posture a failed `sbSave`
// has always had.
// ============================================================================

import type {SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';
import type {SaveStatusSink} from './appStoreBlobApi.js';

/** A ppp-v4 element. Deliberately open — this module reads two fields and copies the rest. */
export type BroilerBatchRow = Record<string, unknown>;

export type FreshBroilerBatchesRead = {ok: true; rows: unknown} | {ok: false; error: SupabaseErrorLike | null};

/** Why a save refused. Surfaced to the operator; never silently swallowed. */
export type BroilerBatchesRefusal =
  | 'read-failed'
  | 'read-malformed'
  | 'base-unkeyed'
  | 'next-unkeyed'
  | 'documents-unmatched'
  // Migration 236: the operations themselves.
  | 'deleted-elsewhere'
  | 'conflict'
  | 'operation-failed';

/**
 * A document list to write onto ONE row, named by id rather than carried as a
 * whole array.
 *
 * This exists so the batch form does not have to hold the ppp-v4 array at all.
 * It used to rebuild the entire blob — `batchRows.map((b) => (b.id === editId ?
 * {...b, documents} : b))` — which is why `legacy.context.batches.batches` was
 * declared by all three broiler surfaces: the form was SUBSCRIBED to App's
 * boot-loaded copy purely to have something to map over. Naming the row instead
 * lets the base come from the save-time read below, which is the only base that
 * is correct on a mount whose legacy boot never ran.
 */
export type BroilerBatchDocumentsPatch = {readonly batchId: unknown; readonly documents: unknown};

export type BroilerBatchesSavePlan =
  | {kind: 'save'; next: unknown[]; carried: unknown[]}
  | {kind: 'unresolved'; reason: BroilerBatchesRefusal};

function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Every identity a ppp-v4 row can be recognised by, namespaced so an `id` can
 * never collide with a `name`.
 *
 * Returns `[]` for a row that has neither, which is the signal both callers use
 * to refuse rather than guess. `0` and `''` are not identities: `String(0)` is a
 * value an operator never sees, and an empty name is what a half-filled draft
 * carries.
 */
export function broilerBatchIdentities(row: unknown): string[] {
  if (!isJsonObject(row)) return [];
  const out: string[] = [];
  const id = row.id;
  if (typeof id === 'string' ? id !== '' : typeof id === 'number' && Number.isFinite(id)) out.push('id:' + String(id));
  const name = row.name;
  if (typeof name === 'string' && name !== '') out.push('name:' + name);
  return out;
}

/**
 * Merge the array a caller already built onto the blob as the server holds it.
 *
 * `next` is authoritative for every row it names. `removed` is the rows the
 * caller deliberately dropped — passed as ROWS, not ids, so the caller cannot
 * accidentally name a removal by an identity the stored row does not carry.
 * Everything else the read returned is carried through unchanged, appended after
 * `next` so that a caller which sorted its own array keeps that order.
 *
 * When the read agrees with the caller — every booted mount with no concurrent
 * writer — `carried` is empty and `next` is returned element-for-element, by
 * identity. That is the property that makes this safe to put under all eight
 * existing call sites without re-deciding what any of them does.
 */
export function resolveBroilerBatchesSavePlan(
  fresh: FreshBroilerBatchesRead | null | undefined,
  next: unknown,
  removed: readonly unknown[] = [],
  documents: BroilerBatchDocumentsPatch | null = null,
): BroilerBatchesSavePlan {
  // A caller that could not produce a result at all is in the same position as
  // one whose query errored: there is no base, so there is no correct write.
  if (!fresh || fresh.ok !== true) return {kind: 'unresolved', reason: 'read-failed'};
  if (!Array.isArray(next)) return {kind: 'unresolved', reason: 'next-unkeyed'};

  // `null`/`undefined` is the ABSENT row — a farm with no broiler history yet,
  // and a legitimate empty base. Anything else that is not an array is a
  // document this module cannot merge into without destroying it.
  let base: unknown[];
  if (fresh.rows == null) base = [];
  else if (Array.isArray(fresh.rows)) base = fresh.rows;
  else return {kind: 'unresolved', reason: 'read-malformed'};

  // Named = every identity the caller's own array accounts for, plus every
  // identity it deliberately dropped. A stored row matching ANY of these is the
  // caller's business and is not carried.
  const named = new Set<string>();
  for (const row of next) {
    const ids = broilerBatchIdentities(row);
    if (ids.length === 0) return {kind: 'unresolved', reason: 'next-unkeyed'};
    for (const id of ids) named.add(id);
  }
  for (const row of removed) {
    for (const id of broilerBatchIdentities(row)) named.add(id);
  }

  const carried: unknown[] = [];
  for (const row of base) {
    const ids = broilerBatchIdentities(row);
    // A stored row with no identity can be proved neither present nor absent.
    // Carrying it might duplicate a row the caller already has; dropping it
    // might delete farm history. Neither is allowed, so the save refuses.
    if (ids.length === 0) return {kind: 'unresolved', reason: 'base-unkeyed'};
    if (!ids.some((id) => named.has(id))) carried.push(row);
  }

  const merged = carried.length === 0 ? next : [...next, ...carried];
  if (!documents) return {kind: 'save', next: merged, carried};

  // THE DOCUMENT PATCH IS APPLIED TO THE MERGE, NOT TO THE CALLER'S ARRAY, AND
  // THAT IS THE WHOLE POINT. The row it names may have come from `next` (a
  // booted mount, where this is a no-op re-write of a field the caller already
  // set) or from `carried` (a mount with no boot state, where the caller had
  // NOTHING to map over and `next` is empty). Both land in `merged`, so one
  // pass over it covers both without the caller having to know which case it
  // is in — which is exactly what lets the form stop reading the array.
  //
  // A row this patches is a NEW object rather than the one the read produced.
  // That does not weaken the carry-by-reference property the module rests on:
  // the guarantee is that a row nobody NAMED is written back exactly as read,
  // and this row is named — it is the one the operator just attached a file to.
  const targetIdentities = broilerBatchIdentities({id: documents.batchId});
  // An unkeyed target cannot be matched, and must not fall through to a
  // `row.id === undefined` comparison: that is true for EVERY id-less row, so a
  // patch with no batch id would rewrite the documents of all of them. The old
  // whole-array path had exactly that latent bug, in the same shape `del`'s
  // did.
  if (targetIdentities.length === 0) return {kind: 'unresolved', reason: 'documents-unmatched'};

  let matched = 0;
  const patched = merged.map((row) => {
    if (!broilerBatchIdentities(row).some((id) => targetIdentities.includes(id))) return row;
    matched += 1;
    return {...(row as Record<string, unknown>), documents: documents.documents};
  });
  // FAIL CLOSED RATHER THAN SILENTLY DROP THE UPLOAD. The old path expressed a
  // miss as `map` matching nothing, so the file was uploaded to storage, the
  // blob was rewritten without it, and the operator was told nothing. A refusal
  // drives the same error indicator every other refusal here drives.
  if (matched === 0) return {kind: 'unresolved', reason: 'documents-unmatched'};

  return {kind: 'save', next: patched, carried};
}

/**
 * Read ppp-v4 as it is on the server RIGHT NOW.
 *
 * Two unwraps, both load-bearing: `.maybeSingle()` yields the ROW or null, and
 * the row's own `data` column holds the array. A missing row therefore surfaces
 * as `{ok: true, rows: null}` — success, no document.
 *
 * The key is spelled as a LITERAL in the PostgREST chain, deliberately.
 * `app_store_boundary_static` censuses the literal keys reachable in each owner
 * module with a regex that allows at most 120 characters between `.from(` and
 * `.eq(`; hoisting it to a constant, or breaking the chain across more lines,
 * would make that guard blind to this module while every test naming the key
 * still passed. Do not "clean this up".
 */
export async function readFreshBroilerBatches(sb: SupabaseClient): Promise<FreshBroilerBatchesRead> {
  try {
    const r = await sb.from('app_store').select('data').eq('key', 'ppp-v4').maybeSingle();
    if (r.error) return {ok: false, error: r.error};
    return {ok: true, rows: r.data ? r.data.data : null};
  } catch (error) {
    // A rejected read is a failed read. Left uncaught it would reject `persist`,
    // whose callers never had a rejection to handle because App's `sbSave` never
    // threw — and an unhandled rejection here would be indistinguishable from a
    // save that quietly did nothing.
    return {ok: false, error: (error as SupabaseErrorLike) || null};
  }
}

/**
 * The save decision, with the ORDER it depends on made explicit and injectable.
 *
 * The read is awaited BEFORE anything is decided and its result is the only
 * base — which is the fix, stated as a signature rather than as a comment.
 * Modelled on `planProcessorClear` in src/livestock/WeighInSessionPage.tsx and
 * `planFeedOrdersSave` in src/lib/feedOrdersApi.ts for exactly that reason:
 * `readFresh` being a parameter is what lets a test prove the write is built
 * from a payload that arrived AFTER the operator's edit.
 */
export async function planBroilerBatchesSave(
  readFresh: () => Promise<FreshBroilerBatchesRead | null>,
  next: unknown,
  removed: readonly unknown[] = [],
  documents: BroilerBatchDocumentsPatch | null = null,
): Promise<BroilerBatchesSavePlan> {
  return resolveBroilerBatchesSavePlan(await readFresh(), next, removed, documents);
}

/** What the operator is told when a save refuses. One line per refusal, never a generic one. */
const BROILER_BATCHES_REFUSAL_DETAIL: Record<BroilerBatchesRefusal, string> = {
  'read-failed': 'the current blob could not be read',
  'read-malformed': 'the stored blob is not an array',
  'base-unkeyed': 'a stored batch has neither an id nor a name',
  'next-unkeyed': 'a batch being saved has neither an id nor a name',
  'documents-unmatched': 'the batch a document save named is not in the stored blob',
  'deleted-elsewhere': 'the batch being saved was deleted by someone else',
  conflict: 'someone else changed this batch since it was opened',
  'operation-failed': 'the server refused or did not answer the save',
};

/**
 * Report a refusal the only way a failed ppp-v4 write was ever reported.
 *
 * Copied deliberately from `saveAppStoreBlob`'s own failure tail — console
 * record, `error`, cleared after 4000ms — so the header indicator is truthful
 * even when nothing was attempted, and an operator cannot tell a refused save
 * from a failed one by the chrome. They should not have to: both mean "not
 * saved, try again".
 */
export function reportBroilerBatchesRefusal(reason: BroilerBatchesRefusal, onSaveStatus: SaveStatusSink): void {
  console.error('ppp-v4 save refused — ' + BROILER_BATCHES_REFUSAL_DETAIL[reason] + ':', reason);
  onSaveStatus('error');
  setTimeout(() => onSaveStatus(''), 4000);
}
